import { Injectable } from '@angular/core';
import * as d3 from 'd3';
import { Subject } from 'rxjs';
import { InspectionPoint } from '../../../../viewmodels/idms/inspection-point';

@Injectable()
export class DrawingPreviewInspectionPointService {
  private readonly identifierAttributeName = 'identifier';
  private nextIdentifier = 0;
  private identifiersMappings: InspectionPointIndentifierMapping[] = [];

  private svgElement: SVGSVGElement;
  private originalViewBoxWidth: number;

  mouseOverInspectionPoint = new Subject<InspectionPointHover>();

  constructor() { }

  initializePreviewInspectionPoints(svgElement: SVGSVGElement, inspectionPoints: InspectionPoint[]) {
    this.originalViewBoxWidth = svgElement.viewBox.baseVal.width;
    this.svgElement = svgElement;

    inspectionPoints.forEach(inspectionPoint => {
      this.addInspectionPoint(inspectionPoint);
    });
  }

  addInspectionPoint(inspectionPoint: InspectionPoint) {
    if (!this.svgElement) {
      return;
    }

    this.deleteInspectionPoint(inspectionPoint);

    this.nextIdentifier = this.nextIdentifier + 1;
    this.identifiersMappings.push(new InspectionPointIndentifierMapping(inspectionPoint, this.nextIdentifier.toString()));

    this.drawInspectionPoint(inspectionPoint, this.nextIdentifier.toString());
  }

  deleteInspectionPoint(inspectionPoint: InspectionPoint) {
    if (!this.svgElement) {
      return;
    }

    const foundMapping = this.identifiersMappings.find(identifier => identifier.inspectionPoint === inspectionPoint);

    if (!foundMapping) {
      return;
    }

    this.identifiersMappings = this.identifiersMappings.filter(mapping => mapping.inspectionPoint !== inspectionPoint);

    const svgSelection = d3.select(this.svgElement);
    const inspectionPointSelection = svgSelection.selectAll(`[${this.identifierAttributeName}='${foundMapping.identifier}']`);
    inspectionPointSelection.remove();
  }

  reloadInspectionPoints(inspectionPoints: InspectionPoint[]) {
    this.clearInspectionPoints();

    inspectionPoints.forEach(inspectionPoint => this.addInspectionPoint(inspectionPoint));
  }

  private clearInspectionPoints() {
    this.identifiersMappings.forEach(mapping => this.deleteInspectionPoint(mapping.inspectionPoint));
    this.identifiersMappings = [];
  }

  private drawInspectionPoint(inspectionPoint: InspectionPoint, identifier: string) {
    if (!inspectionPoint.svgPosition.isDisplayed) {
      return;
    }

    const svgUnitsPerPixel = this.svgElement.width.baseVal.value / this.originalViewBoxWidth;
    const zoomScale = this.svgElement.viewBox.baseVal.width / this.originalViewBoxWidth;

    const svgSelection = d3.select(this.svgElement);

    // Convert target pixels sizes to SVG units
    const circleRadius = (8 * zoomScale) / svgUnitsPerPixel;
    const strokeWidth = (1 * zoomScale) / svgUnitsPerPixel;
    const textdistance = (24 * zoomScale) / svgUnitsPerPixel;
    const fontSize = (16 * zoomScale) / svgUnitsPerPixel;
    const leaderLineMargin = (2 * zoomScale) / svgUnitsPerPixel;

    const circleSelection = svgSelection.append('circle')
      .attr(this.identifierAttributeName, identifier)
      .attr('cx', inspectionPoint.svgPosition.x)
      .attr('cy', inspectionPoint.svgPosition.y)
      .attr('r', circleRadius)
      .attr('class', 'circle')
      .style('stroke', '#333')
      .style('stroke-width', strokeWidth)
      .style('fill', inspectionPoint.inspectionPointType.color);

    circleSelection.node().addEventListener('mouseenter', (e: any) => {
      const clientRect = e.target.getBoundingClientRect();

      const clientX = clientRect.x + (clientRect.width / 2);
      const clientY = clientRect.y + (clientRect.height / 2);

      const svgRect = this.svgElement.getBoundingClientRect();

      const positionX = clientX - svgRect.left;
      const positionY = clientY - svgRect.top;

      this.mouseOverInspectionPoint.next(new InspectionPointHover(inspectionPoint, positionX, positionY, this.svgElement));
    });

    circleSelection.node().addEventListener('mouseleave', (e: any) => {
      this.mouseOverInspectionPoint.next(null);
    });

    if (!inspectionPoint.name) {
      return;
    }

    svgSelection.append('line')
      .attr(this.identifierAttributeName, identifier)
      .style('stroke', '#333')
      .style('stroke-width', strokeWidth)
      .attr('x1', inspectionPoint.svgPosition.x + circleRadius - strokeWidth)
      .attr('y1', inspectionPoint.svgPosition.y + circleRadius - strokeWidth)
      .attr('x2', inspectionPoint.svgPosition.x + textdistance - leaderLineMargin)
      .attr('y2', inspectionPoint.svgPosition.y + textdistance - leaderLineMargin);

    svgSelection.append('text')
      .attr(this.identifierAttributeName, identifier)
      .attr('dx', inspectionPoint.svgPosition.x + textdistance)
      .attr('dy', inspectionPoint.svgPosition.y + textdistance)
      .attr('text-anchor', 'start')
      .attr('alignment-baseline', 'hanging')
      .attr('font-size', fontSize)
      .text(inspectionPoint.name);
  }
}

class InspectionPointIndentifierMapping {
  constructor(public inspectionPoint: InspectionPoint, public identifier: string) { }
}

export class InspectionPointHover {
  constructor(public inspectionPoint: InspectionPoint, public clientX: number, public clientY: number, public svgElement: SVGSVGElement) { }
}
